import { PayloadI } from "../../pojo/EventTypeBulkEditPayloadI";
import { notificationPolicyI } from "../../pojo/HierarchyI";
import { Proto } from "../../pojo/Proto";
import { Task } from "../../pojo/Task";
import { S25Util } from "../../util/s25-util";
import { NotificationPolicyModel } from "./notification.policy.model";

export namespace NotificationPolicyMapper {
    export const REMOVED_ID = 0;
    export type WireContactRow = NotificationPolicyModel.MapperWireContactRow;

    export function roleToNotifyTypeId(role: NotificationPolicyModel.ContactRole): number {
        return role === "approver" ? Task.Ids.Authorization : Task.Ids.FYI;
    }

    export function notifyTypeIdToRole(notifyTypeId: number | undefined): NotificationPolicyModel.ContactRole | null {
        if (notifyTypeId === Task.Ids.Authorization) {
            return "approver";
        }
        if (notifyTypeId === Task.Ids.FYI) {
            return "fyi";
        }
        return null;
    }

    export function isRemovedPolicy(draft: NotificationPolicyModel.FormModel): boolean {
        return !!draft.removedPolicy || draft.approvalType?.itemId === REMOVED_ID;
    }

    export function assignmentToWireContact(
        assignment: NotificationPolicyModel.Assignment,
    ): NotificationPolicyModel.WireContactIdentity {
        return {
            contact_id: assignment.contactId,
            notify_type: roleToNotifyTypeId(assignment.role),
        };
    }

    /** ANG-5254: Unknown quorum ids are omitted so the replace payload is not built. */
    export function approverQuorumBulkName(itemId: number | undefined): "One" | "All" | undefined {
        if (itemId === Task.NotificationPolicy.ApproverQuorum.Any) {
            return "One";
        }
        if (itemId === Task.NotificationPolicy.ApproverQuorum.All) {
            return "All";
        }
        return undefined;
    }

    export function emptyDraft(): NotificationPolicyModel.FormModel {
        return { assignments: [], days: 0, hours: 0, minutes: 0, removedPolicy: false };
    }

    export function emptyDraftWithDefaults(): NotificationPolicyModel.FormModel {
        return {
            ...emptyDraft(),
            approvalType: S25Util.deepCopy(Task.NotificationPolicy.DefaultApproverQuorumModel),
        };
    }

    export function draftHasPolicyContent(draft: NotificationPolicyModel.FormModel): boolean {
        if (draft.removedPolicy) {
            return false;
        }
        if (draft.approvalType?.itemId) {
            return true;
        }
        return draft.assignments.length > 0;
    }

    export function deepCopyDraft(draft: NotificationPolicyModel.FormModel): NotificationPolicyModel.FormModel {
        return {
            ...draft,
            assignments: draft.assignments.map((assignment) => ({ ...assignment })),
            approvalType: draft.approvalType ? { ...draft.approvalType } : draft.approvalType,
        };
    }

    /** Defaults for signal-form binding (numeric fields, empty approval object). */
    export function draftForForm(draft: NotificationPolicyModel.FormModel): NotificationPolicyModel.FormBindingModel {
        return {
            assignments: draft.assignments.map((assignment) => ({ ...assignment })),
            approvalType: draft.approvalType ? { ...draft.approvalType } : {},
            days: draft.days ?? 0,
            hours: draft.hours ?? 0,
            minutes: draft.minutes ?? 0,
            removedPolicy: !!draft.removedPolicy,
        };
    }

    /** Normalize form state back to draft semantics (quorum labels, removed flag). */
    export function draftFromForm(model: NotificationPolicyModel.FormModel): NotificationPolicyModel.FormModel {
        const parsedApprovalTypeId = S25Util.parseInt(model.approvalType?.itemId);
        const approvalTypeId =
            parsedApprovalTypeId != null && Number.isFinite(parsedApprovalTypeId) ? parsedApprovalTypeId : null;
        let approvalType: NotificationPolicyModel.ApprovalType | undefined;
        if (approvalTypeId === REMOVED_ID) {
            approvalType = { itemName: "No Notification Policy", itemId: REMOVED_ID };
        } else if (approvalTypeId != null) {
            const match = S25Util.array.getByProp(
                Task.NotificationPolicy.ApproverQuorumOptions,
                "itemId",
                approvalTypeId,
            );
            approvalType = match ? S25Util.deepCopy(match) : { ...model.approvalType, itemId: approvalTypeId };
        } else if (model.approvalType?.itemName) {
            approvalType = { ...model.approvalType };
        }
        return {
            assignments: model.assignments.map((assignment) => ({ ...assignment })),
            approvalType,
            days: S25Util.parseInt(model.days) ?? 0,
            hours: S25Util.parseInt(model.hours) ?? 0,
            minutes: S25Util.parseInt(model.minutes) ?? 0,
            removedPolicy: !!model.removedPolicy || approvalTypeId === REMOVED_ID,
        };
    }

    export function quorumReadOnlyLabel(draft: NotificationPolicyModel.FormModel): string | null {
        const approverCount = draft.assignments.filter((assignment) => assignment.role === "approver").length;
        if (approverCount < 2) {
            return null;
        }
        if (draft.approvalType?.itemId === Task.NotificationPolicy.ApproverQuorum.All) {
            return "When multiple approvers: all approvers must approve.";
        }
        return "When multiple approvers: any one approver can approve.";
    }

    export function sessionFromDraft(
        draft: NotificationPolicyModel.FormModel,
        isNewPolicy: boolean,
    ): NotificationPolicyModel.Session {
        const copy = deepCopyDraft(draft);
        return {
            draft: copy,
            baseline: isNewPolicy ? null : deepCopyDraft(copy),
            isNewPolicy,
        };
    }

    export function draftsEqual(
        left: NotificationPolicyModel.FormModel,
        right: NotificationPolicyModel.FormModel,
    ): boolean {
        if (!!left.removedPolicy !== !!right.removedPolicy) {
            return false;
        }
        if ((left.days ?? 0) !== (right.days ?? 0)) {
            return false;
        }
        if ((left.hours ?? 0) !== (right.hours ?? 0)) {
            return false;
        }
        if ((left.minutes ?? 0) !== (right.minutes ?? 0)) {
            return false;
        }
        if ((left.approvalType?.itemId ?? null) !== (right.approvalType?.itemId ?? null)) {
            return false;
        }
        if (left.assignments.length !== right.assignments.length) {
            return false;
        }
        const leftSorted = [...left.assignments].sort(
            (leftAssignment, rightAssignment) => leftAssignment.contactId - rightAssignment.contactId,
        );
        const rightSorted = [...right.assignments].sort(
            (leftAssignment, rightAssignment) => leftAssignment.contactId - rightAssignment.contactId,
        );
        for (let i = 0; i < leftSorted.length; i++) {
            if (leftSorted[i].contactId !== rightSorted[i].contactId) {
                return false;
            }
            if (leftSorted[i].role !== rightSorted[i].role) {
                return false;
            }
        }
        return true;
    }

    export function sessionFromGetResponse(
        data: NotificationPolicyModel.ApiGetPolicy | null | undefined,
    ): NotificationPolicyModel.Session {
        return sessionFromDraft(draftFromGetResponse(data), !data);
    }

    export function draftFromGetResponse(
        data: NotificationPolicyModel.ApiGetPolicy | null | undefined,
    ): NotificationPolicyModel.FormModel {
        const draft = emptyDraft();
        if (!data) {
            return draft;
        }

        const approvalTypeId = S25Util.parseInt(S25Util.propertyGet(data, "approval_type"));
        if (approvalTypeId) {
            const approvalTypeModel = S25Util.array.getByProp(
                Task.NotificationPolicy.ApproverQuorumOptions,
                "itemId",
                approvalTypeId,
            );
            draft.approvalType = S25Util.deepCopy(approvalTypeModel);
        }

        const duration = S25Util.ISODurationToObj(data?.respond_within ?? "");
        draft.days = duration.days;
        draft.hours = duration.hours;
        draft.minutes = duration.minutes;

        const contactRows = data?.contact;
        if (contactRows?.length) {
            for (const contactRow of contactRows) {
                const assignment = mapApiContactRowToAssignment(contactRow);
                if (assignment) {
                    draft.assignments.push(assignment);
                }
            }
        }

        return draft;
    }

    export function draftFromPolicyLike(
        source: NotificationPolicyModel.PolicyLikeSource | null | undefined,
    ): NotificationPolicyModel.FormModel {
        if (!source) {
            return emptyDraft();
        }
        const approvalType = source.approval_type ?? source.approval_from;
        const rawContacts = source.contacts ?? source.contact;
        const contactRows = rawContacts
            ? S25Util.array.isArray(rawContacts)
                ? rawContacts
                : [rawContacts]
            : undefined;
        return draftFromGetResponse({
            approval_type: approvalType,
            respond_within: source.respond_within,
            contact: contactRows,
        });
    }

    export function mapApiContactRowToAssignment(
        contactRow: NotificationPolicyModel.ApiGetContactRow,
    ): NotificationPolicyModel.Assignment | null {
        const notifyTypeId = S25Util.parseInt(S25Util.propertyGet(contactRow, "notify_type"));
        const role = notifyTypeIdToRole(notifyTypeId);
        const contactId = S25Util.parseInt(contactRow.contact_id);
        if (role == null || contactId == null) {
            return null;
        }
        return {
            contactId,
            contactName: contactRow.contact_name ?? "",
            role,
        };
    }

    export function assignmentToContactDisplay(
        assignment: NotificationPolicyModel.Assignment,
    ): NotificationPolicyModel.ContactDisplay {
        const notifyTypeId = roleToNotifyTypeId(assignment.role);
        const notifyTypeModel =
            notifyTypeId === Task.Ids.FYI
                ? Task.NotificationPolicy.NotifyFyiModel
                : Task.NotificationPolicy.NotifyApprovalModel;
        return {
            itemId: assignment.contactId,
            itemName: assignment.contactName,
            notifyType: S25Util.deepCopy(notifyTypeModel),
        };
    }

    export function assignmentMap(
        draft: NotificationPolicyModel.FormModel,
    ): Map<number, NotificationPolicyModel.Assignment> {
        const map = new Map<number, NotificationPolicyModel.Assignment>();
        for (const assignment of draft.assignments) {
            map.set(assignment.contactId, assignment);
        }
        return map;
    }

    export function diffToWireContacts(
        draft: NotificationPolicyModel.FormModel,
        baseline: NotificationPolicyModel.FormModel | null,
    ): WireContactRow[] {
        const baselineMap = assignmentMap(baseline ?? emptyDraft());
        const draftMap = assignmentMap(draft);
        const allContactIds = new Set<number>([...baselineMap.keys(), ...draftMap.keys()]);
        const wire: WireContactRow[] = [];

        for (const contactId of allContactIds) {
            const baselineAssignment = baselineMap.get(contactId);
            const draftAssignment = draftMap.get(contactId);

            if (draftAssignment && !baselineAssignment) {
                wire.push({
                    status: "new",
                    ...assignmentToWireContact(draftAssignment),
                });
                continue;
            }

            if (!draftAssignment && baselineAssignment) {
                wire.push({
                    status: "del",
                    ...assignmentToWireContact(baselineAssignment),
                });
                continue;
            }

            if (draftAssignment && baselineAssignment) {
                const status = draftAssignment.role === baselineAssignment.role ? "est" : "mod";
                wire.push({
                    status,
                    ...assignmentToWireContact(draftAssignment),
                });
            }
        }

        return wire;
    }

    export function ensureDefaultQuorum(draft: NotificationPolicyModel.FormModel): NotificationPolicyModel.FormModel {
        const nextDraft = deepCopyDraft(draft);
        if (!nextDraft.approvalType?.itemId) {
            nextDraft.approvalType = S25Util.deepCopy(Task.NotificationPolicy.DefaultApproverQuorumModel);
        }
        return nextDraft;
    }

    export function validateDraft(
        draft: NotificationPolicyModel.FormModel,
    ): NotificationPolicyModel.DraftValidationResult {
        const nextDraft = deepCopyDraft(draft);
        if (nextDraft.removedPolicy) {
            return { valid: true, message: "", draft: nextDraft };
        }

        if (!nextDraft.assignments.length) {
            return {
                valid: false,
                message: "Please add a contact to this policy",
                draft: nextDraft,
            };
        }

        const approverCount = nextDraft.assignments.filter((assignment) => assignment.role === "approver").length;

        if (approverCount >= 2 && !nextDraft.approvalType?.itemId) {
            return {
                valid: false,
                message: "Please select how multiple approvers must approve",
                draft: nextDraft,
            };
        }

        if (!nextDraft.approvalType?.itemId) {
            return { valid: true, message: "", draft: ensureDefaultQuorum(nextDraft) };
        }

        return { valid: true, message: "", draft: nextDraft };
    }

    export function eventTypeNotificationsFromDraft(
        draft: NotificationPolicyModel.FormModel,
        existing: notificationPolicyI | undefined,
    ): notificationPolicyI | undefined {
        if (isRemovedPolicy(draft)) {
            if (!existing) {
                return undefined;
            }
            return { ...existing, status: "del" };
        }
        if (draft.approvalType?.itemId == null) {
            return undefined;
        }

        const validation = validateDraft(draft);
        if (!validation.valid) {
            return undefined;
        }
        const validatedDraft = validation.draft;
        const policyStatus: Proto.ServiceStatus = existing ? "mod" : "new";
        const contactStatus: Proto.ServiceStatus = existing ? "mod" : "new";
        const contacts = validatedDraft.assignments.map((assignment) => ({
            contact_name: assignment.contactName,
            status: contactStatus,
            ...assignmentToWireContact(assignment),
        }));

        const notifications: notificationPolicyI = {
            approval_from: validatedDraft.approvalType?.itemId,
            status: policyStatus,
        };
        if (contacts.length) {
            notifications.contacts = contacts;
        }
        const approvalTypeId = validatedDraft.approvalType?.itemId;
        if (approverQuorumBulkName(approvalTypeId)) {
            notifications.respond_within = S25Util.daysHoursMinutesToDuration({
                days: validatedDraft.days || 0,
                hours: validatedDraft.hours || 0,
                minutes: validatedDraft.minutes || 0,
            });
        }
        return notifications;
    }

    export function commitSessionAfterSave(session: NotificationPolicyModel.Session): NotificationPolicyModel.Session {
        const draft = deepCopyDraft(session.draft);
        draft.removedPolicy = false;
        return {
            draft,
            baseline: deepCopyDraft(draft),
            isNewPolicy: false,
        };
    }

    export function buildEventTypeBulkRemovePayload(itemIds: number[]): PayloadI {
        return { idList: itemIds, remove: { notificationPolicy: "" } };
    }

    export function buildEventTypeBulkReplacePayload(
        itemIds: number[],
        draft: NotificationPolicyModel.FormModel,
    ): PayloadI | undefined {
        if (!draft.assignments.length) {
            return undefined;
        }
        const approvalTypeId = draft.approvalType?.itemId;
        const approvalTypeName = approverQuorumBulkName(approvalTypeId);
        if (approvalTypeId == null || !approvalTypeName) {
            return undefined;
        }
        return {
            idList: itemIds,
            notificationPolicy: {
                approvalType: approvalTypeId,
                approvalTypeName,
                respondWithin: S25Util.daysHoursMinutesToDuration({
                    days: draft.days || 0,
                    hours: draft.hours || 0,
                    minutes: draft.minutes || 0,
                }),
                contacts: draft.assignments.map((assignment) => assignmentToEventTypeBulkContact(assignment)),
            },
        };
    }

    export function buildEventTypeBulkContactChangePayload(options: {
        itemIds: number[];
        mode: "add" | "remove";
        assignments?: NotificationPolicyModel.Assignment[];
        contactIds?: number[];
    }): PayloadI {
        if (options.mode === "remove") {
            const contacts = (options.contactIds ?? []).map((contactId) => ({ contId: contactId }));
            return { idList: options.itemIds, notificationPolicy: { remove: { contacts } } };
        }

        const contacts = (options.assignments ?? []).map((assignment) => assignmentToEventTypeBulkContact(assignment));
        return { idList: options.itemIds, notificationPolicy: { contacts } };
    }

    function assignmentToEventTypeBulkContact(assignment: NotificationPolicyModel.Assignment): {
        contId: number;
        notifyType: number;
        notifyTypeName: "FYI" | "approval";
    } {
        const notifyTypeId = roleToNotifyTypeId(assignment.role);
        return {
            contId: assignment.contactId,
            notifyType: notifyTypeId,
            notifyTypeName: notifyTypeId === Task.Ids.FYI ? "FYI" : "approval",
        };
    }
}
